import { getFogAreas, type TiledMap } from '@veriverse/map-format';
import { TILE_SIZE } from '@veriverse/shared';
import type Phaser from 'phaser';
import { FOG_TEXTURE_SIZE, fogPixels } from './fog-math.js';

/** Over every tile layer including decor_above, under the night shade (see `lighting-layer.ts`). */
const FOG_DEPTH = 1400;
const FOG_TEXTURE = 'fog:mist';

/** Two veils drift in different directions, so the mist swirls instead of sliding as one sheet. */
const VEILS = [
  { scale: 1, alpha: 0.8, vx: 5, vy: 1.5 },
  { scale: 1.7, alpha: 0.6, vx: -3, vy: -2 },
] as const;

/**
 * Light, shadowy fog over the rectangles of the map's `fog` layer: areas players cannot reach. The
 * mist drifts slowly; with reduced motion it holds still.
 */
export class FogLayer {
  private readonly veils: { sprite: Phaser.GameObjects.TileSprite; vx: number; vy: number }[] = [];
  private readonly onUpdate = (_time: number, delta: number): void => {
    for (const veil of this.veils) {
      veil.sprite.tilePositionX += (veil.vx * delta) / 1000;
      veil.sprite.tilePositionY += (veil.vy * delta) / 1000;
    }
  };

  constructor(
    private readonly scene: Phaser.Scene,
    map: TiledMap,
    reducedMotion: boolean,
  ) {
    const areas = getFogAreas(map);
    if (areas.length === 0) return;
    if (!scene.textures.exists(FOG_TEXTURE)) {
      const texture = scene.textures.createCanvas(FOG_TEXTURE, FOG_TEXTURE_SIZE, FOG_TEXTURE_SIZE);
      const context = texture?.getContext();
      if (!texture || !context) return;
      const pixels = fogPixels();
      context.putImageData(
        new ImageData(new Uint8ClampedArray(pixels), FOG_TEXTURE_SIZE, FOG_TEXTURE_SIZE),
        0,
        0,
      );
      texture.refresh();
    }
    for (const area of areas) {
      VEILS.forEach((veil, index) => {
        const sprite = scene.add
          .tileSprite(
            area.x * TILE_SIZE,
            area.y * TILE_SIZE,
            area.width * TILE_SIZE,
            area.height * TILE_SIZE,
            FOG_TEXTURE,
          )
          .setOrigin(0, 0)
          .setDepth(FOG_DEPTH + index * 0.01)
          .setAlpha(veil.alpha)
          .setTileScale(veil.scale, veil.scale);
        // Spread the start so neighbouring rectangles do not show the same cloud.
        sprite.tilePositionX = (area.id * 61 + index * 97) % FOG_TEXTURE_SIZE;
        sprite.tilePositionY = (area.id * 37 + index * 53) % FOG_TEXTURE_SIZE;
        this.veils.push({ sprite, vx: veil.vx, vy: veil.vy });
      });
    }
    if (!reducedMotion) scene.events.on('update', this.onUpdate);
  }

  destroy(): void {
    this.scene.events.off('update', this.onUpdate);
  }
}
